import React from 'react';
import {
  ServiceIconShell,
  ServiceIcon as DefaultServiceIcon,
} from '@zapier/design-system';

export type ServiceIconSize = 20 | 30 | 40 | 60 | 80 | 120 | 150;

type Props = {
  /**
   * Applies a custom alt attribute to the underlying Img component if a `src` value isn't provided.
   */
  alt?: string;
  /**
   * Inform screen-readers that they should ignore the image. Image will still be visually displayed. Only when `src` value isn't provided
   *
   * @default false
   */
  ariaHidden?: boolean;
  /**
   * Controls the presence/absence of the border. It is recommended that a borderless ServiceIcon be contained in a larger, bordered container.
   *
   * @default true
   */
  hasBorder?: boolean;
  /**
   * Applies a shadow to the ServiceIcon.
   */
  hasShadow?: boolean;
  /**
   * The name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   * If `src` isn't provided, `serviceName` is used to try and find and render the icon image via CSS classes.
   */
  serviceName: string;
  /**
   * Sets the width and height of the icon (since it is a square), in pixels.
   * The list of possible sizes is finite to enforce consistency across our UI.
   */
  size: ServiceIconSize;
  /**
   *A URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  src?: string;
};

const getPropsWithDefaults = (props: Props) => ({
  ariaHidden: false,
  hasBorder: true,
  ...props,
});

function stripVersion(key: string | null | undefined) {
  key = key || '';
  return key.split('@')[0];
}

/**
 * We're using className to generate the
 *
 * Tags: services, icons
 */
export const ServiceIcon = (_props: Props) => {
  const props = getPropsWithDefaults(_props);
  const {
    alt,
    ariaHidden,
    hasBorder,
    hasShadow,
    serviceName,
    size,
    src,
  } = props;
  const iconSize = Math.ceil(0.7 * size);
  const serviceNameNoVersion = stripVersion(serviceName);

  return (
    <div>
      {src ? (
        <DefaultServiceIcon
          alt={alt}
          ariaHidden={ariaHidden}
          hasBorder={hasBorder}
          hasShadow={hasShadow}
          serviceName={serviceName}
          size={size}
          src={src}
        />
      ) : (
        <ServiceIconShell
          hasBorder={hasBorder}
          hasShadow={hasShadow}
          size={size}
        >
          <div
            className={`PlaceholderAPI64x64 ${serviceNameNoVersion}64x64 app-logo img64 zap-icons__icon`}
            style={{ height: iconSize, width: iconSize }}
          />
        </ServiceIconShell>
      )}
    </div>
  );
};
